import { Box, Text, TextInput } from '@kluein/klue-ui';
import { MagnifyingGlass } from '@phosphor-icons/react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Logo } from 'components/common/logo';
import {
  type DiscoverCompanyType,
  useGetDiscoverCompaniesQuery,
} from 'store/api/discover/discover.api';
import TEST_IDS from 'test-ids';

type CompanyPickerProps = {
  onPick: (company: { url: string; name: string } | null) => void;
  onChangeName: (name: string) => void;
};
export function CompanyPicker({ onChangeName, onPick }: CompanyPickerProps) {
  const { t } = useTranslation();
  const [suppressSuggestions, setSuppressSuggestions] = useState(false);
  const [name, setName] = useState('');
  const { suggestions } = useGetDiscoverCompaniesQuery(
    { name: name },
    {
      skip: !name,
      refetchOnMountOrArgChange: true,
      selectFromResult: ({ data }) => {
        const suggestions: {
          value: DiscoverCompanyType | null;
          label: React.ReactNode;
        }[] =
          data?.items.map((item) => ({
            value: item,
            label: <CompanyPickerLabel {...item} />,
          })) || [];
        if (name) {
          suggestions.push({
            value: null,
            label: (
              <Box
                pad="medium"
                data-test-id={TEST_IDS.boardWizard.companyPickerNotListed}
              >
                <Text>{t('Common:boardWizard.companyPicker.notListed')}</Text>
              </Box>
            ),
          });
        }

        return {
          suggestions,
        };
      },
    },
  );

  function handleSetName(name: string) {
    setName(name);
    onChangeName(name);
  }

  function handleChangeName(event: React.ChangeEvent<HTMLInputElement>) {
    handleSetName(event.target.value);
    setSuppressSuggestions(false);
  }

  return (
    <>
      <TextInput
        value={name}
        placeholder={t('Common:boardWizard.companyPicker.placeholder')}
        onChange={handleChangeName}
        suggestions={suppressSuggestions ? undefined : suggestions}
        onSuggestionSelect={({ suggestion }) => {
          if (suggestion.value === null) {
            onPick(null);
          } else {
            handleSetName(suggestion.value.name);
            onPick({
              url: suggestion.value.domain,
              name: suggestion.value.name,
            });
          }
          setTimeout(() => setSuppressSuggestions(true), 100);
        }}
        data-test-id={TEST_IDS.boardWizard.companyPickerTextInput}
        icon={<MagnifyingGlass />}
        reverse
      />
    </>
  );
}

function CompanyPickerLabel(company: DiscoverCompanyType) {
  return (
    <Box
      pad="xsmall"
      direction="row"
      align="center"
      data-test-id={TEST_IDS.boardWizard.companyPickerSuggestionContainer}
    >
      {company.logo ? (
        <Logo src={company.logo} />
      ) : (
        <Box width="32px" height="32px" />
      )}
      <Box pad="xsmall">
        <Text>{company.name}</Text>
        <Text>{company.domain}</Text>
      </Box>
    </Box>
  );
}
